// Saved graph CRUD for localStorage persistence.
// Mirrors the pure-function pattern of lib/sessions.ts.

import type { SavedGraph, AccumulatedGraph } from '../types';

const GRAPHS_KEY = 'malpedia-saved-graphs';

/** Load all saved graphs from localStorage. */
export function loadSavedGraphs(): SavedGraph[] {
  try {
    const raw = localStorage.getItem(GRAPHS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as SavedGraph[]) : [];
  } catch {
    return [];
  }
}

/** Persist saved graphs array to localStorage. */
export function saveSavedGraphs(graphs: SavedGraph[]): void {
  localStorage.setItem(GRAPHS_KEY, JSON.stringify(graphs));
}

/** Create a new saved graph entry. Returns the new SavedGraph. */
export function createSavedGraph(
  graph: AccumulatedGraph,
  title: string,
  seedEntity?: string,
  seedLabel?: string,
): SavedGraph {
  const now = Date.now();
  return {
    id: crypto.randomUUID(),
    title,
    createdAt: now,
    updatedAt: now,
    graph,
    seedEntity,
    seedLabel,
  };
}

/** Remove a saved graph by id. */
export function deleteSavedGraph(graphs: SavedGraph[], id: string): SavedGraph[] {
  return graphs.filter((g) => g.id !== id);
}

/** Rename a saved graph. */
export function renameSavedGraph(graphs: SavedGraph[], id: string, title: string): SavedGraph[] {
  return graphs.map((g) => (g.id === id ? { ...g, title } : g));
}

/** Update graph data and bump updatedAt. */
export function updateSavedGraphData(
  graphs: SavedGraph[],
  id: string,
  graph: AccumulatedGraph,
): SavedGraph[] {
  return graphs.map((g) =>
    g.id === id ? { ...g, graph, updatedAt: Date.now() } : g,
  );
}

/** Derive a short title from the seed entity or first node name. */
export function deriveGraphTitle(graph: AccumulatedGraph, seedEntity?: string): string {
  if (seedEntity) {
    return seedEntity.length <= 50 ? seedEntity : seedEntity.slice(0, 50).trimEnd();
  }
  const first = graph.nodes[0];
  if (!first) return 'Untitled graph';
  return first.name.length <= 50 ? first.name : first.name.slice(0, 50).trimEnd();
}

/** Remove all saved graphs from localStorage. */
export function clearAllSavedGraphs(): void {
  localStorage.removeItem(GRAPHS_KEY);
}
